









































import type { PropType } from 'vue';
import { defineComponent, computed, ref } from '@nuxtjs/composition-api';
import ThumbnailCarouselItem from '~/components/molecules/Product/ThumbnailCarouselItem/ThumbnailCarouselItem.vue';
import type { ThumbnailItem } from '~/types/components/ThumbnailCarousel/ThumbnailItem';
import { useThumbnailCarouselClicks } from '~/composables';
import CarouselArrowButton from '~/components/atoms/Buttons/CarouselArrowButton/CarouselArrowButton.vue';

export default defineComponent({
  name: 'PdpThumbnailCarousel',
  components: {
    ThumbnailCarouselItem,
    CarouselArrowButton,
  },
  props: {
    sliderItems: {
      type: Array as PropType<Array<ThumbnailItem>>,
      required: true,
    },
    showThreeSixty: {
      type: Boolean,
      required: true,
    },
    videoUrl: {
      type: String,
      required: true,
    },
    videoAlt: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const currentTranslateY = ref(0);
    const thumbnailDimension = 104;
    const thumbnailGap = 8;
    const currentIndex = ref(0);
    const maxSliderItemsDisplayed = 5;

    const carouselWidth = computed(() => `${thumbnailDimension}px`);
    const visibleItemsCount = computed(() => Math.min(maxSliderItemsDisplayed, modifiedSliderItems.value.length));

    const modifiedSliderItems = computed(() => {
      const items = [...props.sliderItems];
      if (props.videoUrl) {
        const videoItem = {
          mobile: { url: props.videoUrl },
          alt: props.videoAlt,
          isVideo: true,
          videoThumbnail: props.videoUrl,
          originalIndex: props.sliderItems.length,
        };
        if (items.length >= 4) {
          items.splice(3, 0, videoItem);
        } else {
          items.push(videoItem);
        }
      }
      if (props.showThreeSixty && items.length > 0) {
        const threeSixtyItem = {
          ...items[0],
          isThreeSixty: true,
          alt: `${items[0].alt} (360°)`,
        };
        if (items.length > 3) {
          items.splice(3, 0, threeSixtyItem);
        } else {
          items.push(threeSixtyItem);
        }
      }
      return items;
    });

    const { handlePrevClickHelper, handleNextClickHelper, carouselArea } = useThumbnailCarouselClicks({
      thumbnailGap,
      thumbnailDimension,
      visibleItemsCount: maxSliderItemsDisplayed,
      sliderItemsLength: modifiedSliderItems.value.length,
    });

    const carouselStyles = computed(() => ({
      transform: `translateY(${currentTranslateY.value}px)`,
      transition: 'transform 0.5s ease',
    }));

    const hasPrev = computed(() => currentIndex.value > 0);
    const hasNext = computed(() => (currentIndex.value + visibleItemsCount.value) < modifiedSliderItems.value.length);

    const hasArrows = computed(() => modifiedSliderItems.value.length > 5);

    const handlePrevClick = () => {
      const clickResult = handlePrevClickHelper(currentIndex.value);
      if (clickResult) {
        currentIndex.value = clickResult.newCurrentIndex;
        currentTranslateY.value = clickResult.newPlacement;
      }
    };

    const handleNextClick = () => {
      const clickResult = handleNextClickHelper(currentIndex.value);
      if (clickResult) {
        currentIndex.value = clickResult.newCurrentIndex;
        currentTranslateY.value = clickResult.newPlacement;
      }
    };

    const handleItemClick = (item: ThumbnailItem) => {
      if (props.showThreeSixty && item.isThreeSixty) {
        emit('threeSixty-click');
        return;
      }
      emit('item-click', { index: item.originalIndex });
    };

    return {
      handlePrevClick,
      handleNextClick,
      handleItemClick,
      carouselWidth,
      carouselStyles,
      hasPrev,
      hasNext,
      hasArrows,
      modifiedSliderItems,
      carouselArea,
      thumbnailDimension,
    };
  },
});
